/**
 * Story Map Page (EPIC 5)
 * 
 * User Story Map visualization — requirements grouped by domain (activity)
 * and module (step), with drill-down detail panel.
 */

import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  ArrowLeft,
  Map as MapIcon,
  Loader2,
  AlertCircle,
  X,
  Copy,
  Check,
  FileText,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface KBDocument {
  id: string;
  title: string;
  content: string;
  type: string;
  domain: string;
  module: string | null;
  status: string;
  tags: string[];
  impact?: { risk_level?: string };
  created_at: string;
  updated_at: string;
}

interface CoverageItem {
  domain: string;
  module: string | null;
  total_documents: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  coverage_score: number;
}

interface StoryCard {
  id: string;
  title: string;
  status: string;
  domain: string;
  module: string | null;
  riskLevel?: string;
  acCount: number;
  content: string;
  tags: string[];
}

interface ActivityGroup {
  domain: string;
  steps: Array<[string, StoryCard[]]>;
  totalCards: number;
}

// ============================================
// Status color mapping
// ============================================

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; label: string }> = {
  active: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Active' },
  draft: { bg: 'bg-neutral-50', text: 'text-neutral-600', border: 'border-neutral-200', label: 'Draft' },
  archived: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Done' },
  deprecated: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', label: 'Deprecated' },
  superseded: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', label: 'Superseded' },
};

function getStatusStyle(status: string) {
  return STATUS_COLORS[status] || STATUS_COLORS.draft;
}

function countAC(content: string): number {
  const matches = content.match(/\[(?:HAPPY|NEGATIVE|EDGE|SECURITY|A11Y|PERF|UC)-?\d*\]/gi);
  return matches?.length || 0;
}

// ============================================
// Main Component
// ============================================

export function StoryMapPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const setHeader = usePageHeaderStore((s) => s.setHeader);
  const [selectedCard, setSelectedCard] = useState<StoryCard | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setHeader('Story Map', 'Visual overview of requirements', <MapIcon className="w-6 h-6" />);
  }, [setHeader]);

  const { data: documents, isLoading: docsLoading, error: docsError } = useQuery<KBDocument[]>({
    queryKey: ['kb-documents', projectId],
    queryFn: async () => {
      const res = await api.get<{ data: KBDocument[] }>(
        `/kb/documents?type=requirement,user_story&limit=200&project_id=${projectId}`
      );
      return res.data || [];
    },
    enabled: !!projectId,
  });

  useQuery<CoverageItem[]>({
    queryKey: ['kb-coverage', projectId],
    queryFn: async () => {
      const res = await api.get<{ coverage: CoverageItem[] }>(`/kb/coverage?project_id=${projectId}`);
      return res.coverage || [];
    },
    enabled: !!projectId,
  });

  // Transform documents into story cards grouped by domain -> module
  const { activities, stats } = useMemo(() => {
    if (!documents) return { activities: [] as ActivityGroup[], stats: { total: 0, done: 0, acTotal: 0 } };

    const cards: StoryCard[] = documents.map(doc => ({
      id: doc.id,
      title: doc.title,
      status: doc.status,
      domain: doc.domain,
      module: doc.module,
      riskLevel: doc.impact?.risk_level,
      acCount: countAC(doc.content),
      content: doc.content,
      tags: doc.tags,
    }));

    const groupMap: Record<string, { stepsMap: Record<string, StoryCard[]>; totalCards: number }> = {};
    for (const card of cards) {
      if (!groupMap[card.domain]) {
        groupMap[card.domain] = { stepsMap: {}, totalCards: 0 };
      }
      const group = groupMap[card.domain];
      const stepKey = card.module || '(default)';
      if (!group.stepsMap[stepKey]) {
        group.stepsMap[stepKey] = [];
      }
      group.stepsMap[stepKey].push(card);
      group.totalCards++;
    }

    const activities: ActivityGroup[] = Object.entries(groupMap)
      .map(([domain, group]) => ({
        domain,
        steps: Object.entries(group.stepsMap),
        totalCards: group.totalCards,
      }))
      .sort((a, b) => b.totalCards - a.totalCards);

    const total = cards.length;
    const done = cards.filter(c => c.status === 'archived').length;
    const acTotal = cards.reduce((sum, c) => sum + c.acCount, 0);

    return { activities, stats: { total, done, acTotal } };
  }, [documents]);

  const handleCopyPrompt = async (docId: string) => {
    try {
      await navigator.clipboard.writeText(
        `Use the get_implementation_prompt tool with query: "${docId}" to get the full implementation context.`
      );
      setCopiedId(docId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* ignore */ }
  };

  if (docsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (docsError) {
    return (
      <div className="p-6">
        <div className="p-4 bg-error-50 border border-error-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error-600" />
          <p className="font-medium text-error-900">Failed to load story map data</p>
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center">
        <div className="py-16">
          <MapIcon className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">No requirements yet</h2>
          <p className="text-neutral-600 mb-6">
            Create your first requirement in Specuno to see it on the Story Map.
          </p>
          <Link
            to={`/app/organizations/${orgId}/projects/${projectId}/whiteboard`}
            className="btn btn-primary"
          >
            Go to Workspace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back + Header */}
      <Link
        to={`/app/organizations/${orgId}/projects/${projectId}`}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Project
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <MapIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Story Map</h1>
            <p className="text-neutral-500 text-sm">
              {stats.done}/{stats.total} requirements done
              {stats.acTotal > 0 && ` · ${stats.acTotal} acceptance criteria`}
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 text-xs">
          {Object.entries(STATUS_COLORS).slice(0, 3).map(([key, style]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${style.bg} ${style.border} border`} />
              <span className="text-neutral-600">{style.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Story Map Grid */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-6 min-w-max">
          {activities.map((activity) => (
            <div key={activity.domain} className="min-w-[280px] max-w-[320px]">
              {/* Activity header (backbone) */}
              <div className="bg-neutral-900 text-white rounded-t-lg px-4 py-3 mb-0">
                <h3 className="font-semibold text-sm uppercase tracking-wide">{activity.domain}</h3>
                <p className="text-xs text-neutral-400">{activity.totalCards} items</p>
              </div>

              {/* Steps */}
              <div className="space-y-0">
                {activity.steps.map(([stepName, cards]: [string, StoryCard[]]) => (
                  <div key={stepName} className="border border-t-0 border-neutral-200 bg-white">
                    {/* Step header */}
                    {stepName !== '(default)' && (
                      <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-200">
                        <span className="text-xs font-medium text-neutral-600">{stepName}</span>
                      </div>
                    )}

                    {/* Cards */}
                    <div className="p-2 space-y-2">
                      {cards.map((card) => {
                        const style = getStatusStyle(card.status);
                        return (
                          <button
                            key={card.id}
                            onClick={() => setSelectedCard(card)}
                            className={`w-full text-left p-3 rounded-lg border transition-all hover:shadow-sm ${style.bg} ${style.border}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-medium text-neutral-900 line-clamp-2">
                                {card.title}
                              </h4>
                              {card.riskLevel && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                                  card.riskLevel === 'critical' ? 'bg-red-100 text-red-700'
                                  : card.riskLevel === 'high' ? 'bg-orange-100 text-orange-700'
                                  : 'bg-neutral-100 text-neutral-600'
                                }`}>
                                  {card.riskLevel}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${style.text} ${style.bg} border ${style.border}`}>
                                {style.label}
                              </span>
                              {card.acCount > 0 && (
                                <span className="text-[10px] text-neutral-500">
                                  {card.acCount} AC
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail Panel (slide-over) */}
      {selectedCard && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setSelectedCard(null)}
          />
          <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
            {/* Panel header */}
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-neutral-900 truncate">
                  {selectedCard.title}
                </h2>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {selectedCard.id} · {selectedCard.domain}
                  {selectedCard.module ? ` / ${selectedCard.module}` : ''}
                </p>
              </div>
              <button
                onClick={() => setSelectedCard(null)}
                className="p-1.5 rounded-lg hover:bg-neutral-100"
              >
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>

            {/* Panel body */}
            <div className="p-6 space-y-6">
              {/* Status + Tags */}
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const style = getStatusStyle(selectedCard.status);
                  return (
                    <span className={`text-xs px-2 py-1 rounded-full ${style.text} ${style.bg} border ${style.border}`}>
                      {style.label}
                    </span>
                  );
                })()}
                {selectedCard.tags.map(tag => (
                  <span key={tag} className="text-xs px-2 py-1 rounded-full bg-neutral-100 text-neutral-600">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Content */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-2">Content</h3>
                <div className="prose prose-sm max-w-none text-neutral-700 bg-neutral-50 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm font-sans">{selectedCard.content}</pre>
                </div>
              </div>

              {/* AC count */}
              {selectedCard.acCount > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-neutral-700 mb-2">
                    Acceptance Criteria ({selectedCard.acCount})
                  </h3>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        selectedCard.status === 'archived' ? 'bg-emerald-500' : 'bg-amber-400'
                      }`}
                      style={{ width: selectedCard.status === 'archived' ? '100%' : '0%' }}
                    />
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    {selectedCard.status === 'archived' ? 'All covered' : 'Pending implementation'}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleCopyPrompt(selectedCard.id)}
                  className="btn bg-primary-50 text-primary hover:bg-primary-100 inline-flex items-center justify-center gap-2 w-full"
                >
                  {copiedId === selectedCard.id ? (
                    <><Check className="w-4 h-4" /> Copied!</>
                  ) : (
                    <><Copy className="w-4 h-4" /> Get Cursor Prompt</>
                  )}
                </button>
                <Link
                  to={`/app/organizations/${orgId}/projects/${projectId}/whiteboard`}
                  className="btn bg-neutral-100 text-neutral-700 hover:bg-neutral-200 inline-flex items-center justify-center gap-2 w-full"
                >
                  <FileText className="w-4 h-4" />
                  Open in Workspace
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoryMapPage;
